import { useEffect, useRef, useState } from 'react';
import {
	Box,
	CircularProgress,
	IconButton,
	Slider,
	Typography,
} from '@mui/material';
import { Pause, Play } from '../assets/icons/Icons';

interface Props {
	url: string;
	isSender: boolean;
}

export default function VoiceMessagePlayer({ url, isSender }: Props) {
	const audioRef = useRef<HTMLAudioElement | null>(null);
	const [playing, setPlaying] = useState(false);
	const [progress, setProgress] = useState(0);
	const [duration, setDuration] = useState(0);
	const [loading, setLoading] = useState(true);
	const color = isSender ? '#FFFFFF' : '#28696D';

	useEffect(() => {
		const audio = audioRef.current;

		if (!audio) return;

		audio.pause();
		audio.currentTime = 0;

		setPlaying(false);
		setProgress(0);
	}, [url]);

	function togglePlay() {
		if (!audioRef.current) return;

		if (playing) {
			audioRef.current.pause();
		} else {
			audioRef.current.play();
		}

		setPlaying(!playing);
	}

	function formatTime(value: number) {
		const minutes = Math.floor(value / 60);
		const seconds = Math.floor(value % 60);

		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	}

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				width: 250,
				gap: 1,
			}}>
			<audio
				ref={audioRef}
				preload="metadata"
				src={url}
				onLoadedMetadata={e => {
					setDuration(e.currentTarget.duration);
					setLoading(false);
				}}
				onCanPlay={() => {
					setLoading(false);
				}}
				onWaiting={() => {
					setLoading(true);
				}}
				onTimeUpdate={e => {
					setProgress(e.currentTarget.currentTime);
				}}
				onEnded={() => {
					const audio = audioRef.current;

					setPlaying(false);

					if (audio) {
						setProgress(audio.duration);

						setTimeout(() => {
							setProgress(0);
						}, 300);
					}
				}}
				onError={e => {
					setLoading(true);
				}}
			/>
			<IconButton
				onClick={togglePlay}
				disabled={loading}
				sx={{
					color: isSender ? '#FFFFFF' : '#28696D',
				}}>
				{loading ? (
					<CircularProgress
						size={20}
						sx={{
							color: isSender ? '#FFFFFF' : '#28696D',
						}}
					/>
				) : playing ? (
					<Pause color={color} />
				) : (
					<Play color={color} />
				)}
			</IconButton>

			<Slider
				size="small"
				value={progress}
				max={duration || 1}
				sx={{
					color: isSender ? '#FFFFFF' : '#28696D',
				}}
				onChange={(_, value) => {
					if (audioRef.current) {
						audioRef.current.currentTime = value as number;
					}
				}}
			/>

			<Typography
				fontSize="11px"
				color={isSender ? '#FFFFFF' : '#000000'}>
				{formatTime(duration)}
			</Typography>
		</Box>
	);
}
